import React from 'react'

/* ------------------------------------------------- */

import 'destyle.css'
import polyfill from  './polyfill'

/* ------------------------------------------------- */

import Detector from './base/Detector'
import Logger from './base/Logger'
import ShopifyClient from './base/ShopifyClient'
import HttpClient from './base/HttpClient'
import Storage from './base/Storage'
import PluginHub from './PluginHub'

import { createFunkGoRoot, matchPage } from './FunkGoRoot'
import { renderHelmetString } from './FunkGoHelmet'

/* ------------------------------------------------- */

import connectPlugins from './connectPlugins'

/* ------------------------------------------------- */

class FunkGo {
  constructor(options = {}) {
    polyfill.install()

    this._initOptions = options

    this.ssr = options.ssr
    this.appInfo = this.createAppInfo(options)
    this.baseHelpers = this.createBaseHelpers(options, this.appInfo)
    this.pluginHub = this.createPluginHub(options, this.appInfo, this.baseHelpers)

    this.registerPlugins(options, this.pluginHub)
    this.registerRouterPlugin(options, this.pluginHub)

  }

  /* ------------------------------------------------- */

  createAppInfo({ info = {} }) {
    if (!info.name || !info.version) {
      throw new Error('must specific name and version at info field')
    }
    return info
  }

  createBaseHelpers({ http, _ua, _hostname, _ip, _contentId, pluginsFactory }, appInfo) {
    const baseOptions = { appInfo, _ua, _hostname, _ip, _contentId }
    const httpOptions = { ...http, ...baseOptions }

    return {
      $http: new HttpClient(httpOptions),
      $logger: new Logger(baseOptions),
      $detector: new Detector(baseOptions),
      $shopify: new ShopifyClient(httpOptions),
      $storage: new Storage(baseOptions),
      $site: new pluginsFactory['site'](baseOptions)
    }
  }

  createPluginHub(options, appInfo, baseHelpers) {
    return new PluginHub({
      options,
      appInfo,
      baseHelpers,
    })
  }

  /* ------------------------------------------------- */

  /* ------------------------------------------------- */

  registerPlugins({ plugins, pluginsFactory, _hostname }, pluginHub) {
    Object.keys(plugins).forEach(pluginName => {
      this.registerPlugin(pluginName, { _hostname, ...plugins[pluginName]}, pluginHub, pluginsFactory[pluginName])
    })
  }

  /* ------------------------------------------------- */

  registerRouterPlugin({ router, pluginsFactory }, pluginHub) {
    this.registerPlugin('router', {
      Factory: pluginsFactory['router'],
      options: router || {} // if no specific router, just pass an empty options
    }, pluginHub)
  }

  /* ------------------------------------------------- */

  registerPlugin(pluginName, pluginValue, pluginHub, pluginFactory) {
    let pluginOptions
    let PluginFactory

    if (typeof pluginValue === 'function') {
      pluginOptions = {}
      PluginFactory = pluginValue
    } else if (typeof pluginValue === 'object' && typeof pluginValue.Factory === 'function') {
      pluginOptions = pluginValue.options
      PluginFactory = pluginValue.Factory
    } else if (pluginFactory) {
      pluginOptions = pluginValue
      PluginFactory = pluginFactory
    }

    if (typeof PluginFactory !== 'function') {
      throw new Error(`${pluginName} is not a valid plugin`)
    }

    pluginHub.register(pluginName, PluginFactory, pluginOptions)
  }

  /* ------------------------------------------------- */
  renderHelmetString(props) {
    return renderHelmetString(props)
  }

  matchComponent(location, router) {
    return matchPage(location, this.pluginHub, router)
  }

  renderRoot({ props, router } = {}) {
    const Root = createFunkGoRoot(this.pluginHub, router)

    return (<Root {...props} />)
  }

  /* ------------------------------------------------- */
  getServerSideRenderEnvKey(options) {
    return this.ssr && typeof this.ssr.getEnvKey === 'function'
      ? this.ssr.getEnvKey(options)
      : false
  }
  /* ------------------------------------------------- */

  start() {
    this.pluginHub.start(connectPlugins)

    return this
  }

  // for ssr, every request will fork a new app instance
  fork(_options) {
    const forkedApp = new FunkGo({
      ...this._initOptions,
      ..._options,
    })
    forkedApp.start()
    return forkedApp
  }
}

export default FunkGo
